// ============================================================================
// IssueMap Helpers
// ============================================================================

import type { Job } from "../../../../../../api/types";
import type { Polygon as GeoJSONPolygon, MultiPolygon, Position } from "geojson";

/**
 * Gets the boundary feature from a job, checking all possible locations
 */
export function getJobBoundaryGeometry(job: Job): GeoJSONPolygon | MultiPolygon | null {
	// Check files.boundaryGeoJSON first (primary location)
	const boundaryFromFiles = job.files?.boundaryGeoJSON;
	if (boundaryFromFiles?.features?.[0]?.geometry) {
		const geom = boundaryFromFiles.features[0].geometry;
		if (geom.type === "Polygon" || geom.type === "MultiPolygon") {
			return geom as GeoJSONPolygon | MultiPolygon;
		}
	}

	// Check fileOptions.boundaryGeoJSON (alternative location)
	const boundaryFromFileOptions = job.fileOptions?.boundaryGeoJSON;
	if (boundaryFromFileOptions?.features?.[0]?.geometry) {
		const geom = boundaryFromFileOptions.features[0].geometry;
		if (geom.type === "Polygon" || geom.type === "MultiPolygon") {
			return geom as GeoJSONPolygon | MultiPolygon;
		}
	}

	return null;
}

/**
 * Convert GeoJSON coordinates to Google Maps LatLngLiteral array
 */
export function convertCoordinatesToPath(coordinates: Position[]): google.maps.LatLngLiteral[] {
	return coordinates.map((coord) => ({
		lat: coord[1],
		lng: coord[0],
	}));
}

/**
 * Extract paths from a GeoJSON Polygon or MultiPolygon geometry
 */
export function extractPathsFromGeometry(geometry: GeoJSONPolygon | MultiPolygon): google.maps.LatLngLiteral[][] {
	if (geometry.type === "Polygon") {
		// For Polygon, return the outer ring (first coordinate array)
		return [convertCoordinatesToPath(geometry.coordinates[0])];
	} else if (geometry.type === "MultiPolygon") {
		// For MultiPolygon, return the outer ring of each polygon
		return geometry.coordinates.map((polygon) => convertCoordinatesToPath(polygon[0]));
	}
	return [];
}

/**
 * Get all coordinates for bounds calculation
 */
export function getAllCoordinatesFromGeometry(geometry: GeoJSONPolygon | MultiPolygon): google.maps.LatLngLiteral[] {
	const coords: google.maps.LatLngLiteral[] = [];
	if (geometry.type === "Polygon") {
		geometry.coordinates[0].forEach((coord) => {
			coords.push({ lat: coord[1], lng: coord[0] });
		});
	} else if (geometry.type === "MultiPolygon") {
		geometry.coordinates.forEach((polygon) => {
			polygon[0].forEach((coord) => {
				coords.push({ lat: coord[1], lng: coord[0] });
			});
		});
	}
	return coords;
}
